import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useOrg } from '../../context/OrgContext';
import {
    getBillingDetails,
    startBillingCheckout,
    createBillingPortalSession,
    createCardSetupIntent,
    setDefaultCard,
    deleteCard,
} from '../../utils/accountApi';
import ConfirmModal from '../ConfirmModal';
import {
    BillingStatusCard,
    PaymentMethodCard,
    AmountDueBanner,
    PaymentHistoryTable,
    PlanSelector,
} from './billing';

export function AccountBillingSection() {
    const { orgId, org } = useOrg();
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [checkoutPlan, setCheckoutPlan] = useState('');
    const [portalLoading, setPortalLoading] = useState(false);
    const [cardBusy, setCardBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const plansRef = useRef(null);

    const loadBilling = useCallback(async () => {
        if (!orgId) return;
        try {
            setError('');
            const data = await getBillingDetails();
            setBilling(data);
        } catch (err) {
            setError(err.message || 'Billing status is unavailable');
        }
    }, [orgId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!orgId) return;
            setLoading(true);
            try {
                const data = await getBillingDetails();
                if (!cancelled) setBilling(data);
            } catch (err) {
                if (!cancelled) setError(err.message || 'Billing status is unavailable');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orgId]);

    async function handleCheckout(plan) {
        try {
            setCheckoutPlan(plan);
            setError('');
            const result = await startBillingCheckout(plan);
            if (result?.checkout_url) {
                window.location.assign(result.checkout_url);
                return;
            }
            throw new Error('Checkout URL was not returned');
        } catch (err) {
            setError(err.message || 'Unable to start Stripe Checkout');
        } finally {
            setCheckoutPlan('');
        }
    }

    async function handleManageBilling() {
        try {
            setPortalLoading(true);
            setError('');
            const result = await createBillingPortalSession();
            if (result?.portal_url) {
                window.location.assign(result.portal_url);
                return;
            }
            throw new Error('Portal URL was not returned');
        } catch (err) {
            setError(err.message || 'Unable to open billing portal');
        } finally {
            setPortalLoading(false);
        }
    }

    function handlePayNow() {
        // If there's a hosted invoice URL, open it; otherwise open portal
        if (billing?.latest_invoice?.hosted_invoice_url) {
            window.open(billing.latest_invoice.hosted_invoice_url, '_blank');
        } else {
            handleManageBilling();
        }
    }

    // No Stripe customer yet → the way to add a card is to pick a plan and enter
    // it at Checkout. Nudge the user to the Plans section.
    function handleAddViaCheckout() {
        plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // After Stripe Elements confirms the SetupIntent, make the validated card the
    // default and refresh so the tab reflects it.
    async function handleCardConfirmed(paymentMethodId) {
        setCardBusy(true);
        setError('');
        try {
            await setDefaultCard(paymentMethodId);
            await loadBilling();
        } catch (err) {
            setError(err.message || 'Could not save the card');
            throw err; // let the inline form surface it too
        } finally {
            setCardBusy(false);
        }
    }

    async function handleDeleteCard() {
        setConfirmDelete(false);
        setCardBusy(true);
        setError('');
        try {
            await deleteCard();
            await loadBilling();
        } catch (err) {
            setError(err.message || 'Could not remove the card');
        } finally {
            setCardBusy(false);
        }
    }

    const status = billing?.access_status || 'unknown';
    const currentPlan = billing?.current_plan || 'free';
    const isLoading = loading || portalLoading || cardBusy;

    return (
        <div>
            <h2 style={styles.title}>Billing</h2>
            <p style={styles.subtitle}>
                Manage this account's SamSurveyor plan, payment method, invoices, and billing history.
            </p>

            {error && (
                <div style={styles.error}>
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {loading ? (
                <div style={styles.loading}>Loading billing status...</div>
            ) : (
                <>
                    {/* Current Billing Status */}
                    <BillingStatusCard
                        billing={billing}
                        orgName={org?.name}
                    />

                    {/* Amount Due Banner (only shows when invoice is open) */}
                    <AmountDueBanner
                        latestInvoice={billing?.latest_invoice}
                        onPayNow={handlePayNow}
                        loading={isLoading}
                    />

                    {/* Payment Method */}
                    <PaymentMethodCard
                        paymentMethod={billing?.payment_method}
                        hasStripeCustomer={!!billing?.has_stripe_customer}
                        onAddViaCheckout={handleAddViaCheckout}
                        createSetupIntent={createCardSetupIntent}
                        onCardConfirmed={handleCardConfirmed}
                        onDeleteCard={() => setConfirmDelete(true)}
                        onManageBilling={handleManageBilling}
                        loading={isLoading}
                    />

                    {/* Payment History */}
                    <PaymentHistoryTable
                        paymentHistory={billing?.payment_history}
                    />

                    {/* Plan Selector */}
                    <div ref={plansRef}>
                        <PlanSelector
                            currentPlan={currentPlan}
                            accessStatus={status}
                            onSelectPlan={handleCheckout}
                            loading={isLoading}
                            checkoutPlan={checkoutPlan}
                        />
                    </div>
                </>
            )}

            <ConfirmModal
                open={confirmDelete}
                title="Remove card?"
                message="Remove the card on file? Without a payment method, your next renewal can't be charged and the account will end at the close of the current billing period."
                confirmLabel="Remove card"
                danger
                onConfirm={handleDeleteCard}
                onCancel={() => setConfirmDelete(false)}
            />
        </div>
    );
}

const styles = {
    title: {
        fontSize: 20,
        fontWeight: 600,
        color: '#E2E8F0',
        margin: '0 0 4px',
    },
    subtitle: {
        fontSize: 13,
        color: '#C3CDDC',
        margin: '0 0 24px',
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 14px',
        color: '#FCA5A5',
        background: 'rgba(239, 68, 68, 0.12)',
        border: '1px solid rgba(239, 68, 68, 0.24)',
        borderRadius: 8,
        marginBottom: 18,
        fontSize: 13,
    },
    loading: {
        color: '#C3CDDC',
        fontSize: 14,
        padding: '16px 0',
    },
};
